import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faHouse, faListCheck, faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import TextField from "@mui/material/TextField";
import {I18n} from "aws-amplify/utils";
import {Box, Popover} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SelectHidingMenu from "./SelectHidingMenu";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import './UserFilteringEngine.css';
import {SimpleTreeView} from "@mui/x-tree-view";
import { styled } from '@mui/material/styles';
import { unstable_useTreeItem2 as useTreeItem2 } from '@mui/x-tree-view/useTreeItem2';
import {
    TreeItem2Content,
    TreeItem2IconContainer,
    TreeItem2GroupTransition,
    TreeItem2Label,
    TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider';
import {getDetailedAccounts} from "../../../graphql/queries";
import GraphQlTool from "../../../Utils/GraphQlTool";
import ConfigAnalyzer from "../../../Utils/ConfigAnalyzer";
import DialogConfirmRemove from "../DialogConfirmRemove/DialogConfirmRemove";
import {Roles, getIconForNode, UserRoles} from '../../../Models/Roles.js';
import {AccountType, fundType} from "../../../Models/Enums";
import PayStationFinder from "../../../Utils/PayStationFinder";
import {extractCustomer, extractReseller, payStationMatchesFilter} from "../SearchBar/PaystationFilteringLogic";
import SearchBar from "../SearchBar/SearchBar";

let isNewFilter = false;
let editingFilterName = null;
let shouldNotify = false;
let isClearRequested = false;
let previousReseller = null;
let previousCustomer = null;

/**
 * @typedef {Object} FilterTreeItemProps
 * @property {string} [id]
 * @property {string} itemId
 * @property {string} label
 * @property {boolean} [disabled]
 * @property {React.ReactNode} [children]
 */

const extractCompanyShopLabel = fullLabel => {
    const firstIndex = fullLabel.indexOf('|');
    if (firstIndex === -1) {
        return fullLabel;
    }

    const secondIndex = fullLabel.indexOf('|', firstIndex + 1);
    if (secondIndex === -1) {
        return fullLabel;
    }

    return fullLabel.substring(0, secondIndex);
}

/**
 * Filtre les installations accessibles selon une recherche ou un filtre enregistré.
 * @param {object} props
 * @param {import('../../../Utils/UserDataManager').default | null} props.user
 * @param {import('../../../Models/DomainTypes').PayStation[]} [props.payStations] Normalized stations, available after loading.
 * @param {{ graphql: Function }} props.API
 * @param {boolean} props.isDarkStyle
 * @param {HTMLElement | null} props.isSearchMenuOpened
 * @param {(payStations: import('../../../Models/DomainTypes').PayStation[]) => void} props.onFilterUpdated
 * @param {(filter: () => import('../../../Models/DomainTypes').PayStation[]) => void} props.registerFilterDelegate
 * @param {(anchor: HTMLElement | null) => void} props.onSearchMenuOpenedStateChanged
 * @param {string} [props.variant]
 * @param {Object} [props.SelectProps]
 */
const UserFilteringEngine = props => {
    const filterIndex = parseInt( props?.user?.userProperties?.properties?.currentFilter , 10 );
    const [isSimpleMode] = React.useState( true );
    const [anchorEl, setAnchorEl] = React.useState( props.isSearchMenuOpened );
    const [currentSearch, setCurrentSearch] = React.useState(null);
    const [filterName, setFilterName] = React.useState(null);
    const [currentFilter, setCurrentFilter] = React.useState(( props?.user?.userProperties?.properties?.currentFilter ) ? props?.user?.userProperties?.properties?.currentFilter : '-1');
    const [isEditing, setIsEditing] = React.useState(false);
    const [pendingDelete, setPendingDelete] = React.useState(false);
    const [selectedItems, setSelectedItems] = React.useState(( props?.user?.userProperties?.properties?.currentFilter &&
        props?.user?.userProperties?.properties?.currentFilter !== '-1' &&
        props?.user?.userProperties?.filters[ filterIndex ] ) ? props?.user?.userProperties?.filters[ filterIndex ].selection : []);
    const [selectedDynamicItems, setSelectedDynamicItems] = React.useState( []);
    const [expandedItems, setExpandedItems] = React.useState(( props?.user?.userProperties?.properties?.currentFilter &&
        props?.user?.userProperties?.properties?.currentFilter !== '-1' &&
        props?.user?.userProperties?.filters[ filterIndex ] ) ? props?.user?.userProperties?.filters[ filterIndex ].expanded : []);
    const [accounts, setAccounts] = React.useState( null );
    const [reseller, setReseller] = React.useState( '-1' );
    const [customer, setCustomer] = React.useState( '-1' );
    const accountsLoadingRef = React.useRef( false );
    const accountId = props.user?.observer?.currentUser?.accountId;
    const configAnalyzer = new ConfigAnalyzer( props.payStations );

    const loadAccounts = React.useCallback(() => {
        if( ! props.API || ! accountId || accountsLoadingRef.current ) {
            return;
        }

        accountsLoadingRef.current = true;
        props.API
            .graphql({ query: getDetailedAccounts, variables: { accountId: accountId }})
            .then( returned => {
                const tool = new GraphQlTool( 'getDetailedAccounts' , returned );
                setAccounts( tool.extract() );
            })
            .catch((error) => {
                console.error("error" , error);
            })
            .finally(() => {
                accountsLoadingRef.current = false;
            });
    }, [props.API, accountId]);

    const filter = () => {
        if( isSimpleMode ) {
            return filterForSimpleMode();
        }
        return filterForAdvancedMode();
    }

    const filterForSimpleMode = () => {
        const filtered = [];
        props.payStations?.forEach( payStation => {
            if( selectedDynamicItems.some( item => item.id === payStation.box?.id ) ) {
                filtered.push( payStation );
            }
        } );
        return filtered;
    }

    const filterForAdvancedMode = () => {
        const filtered = [];
        props.payStations?.forEach( payStation => {
            const shouldDisplayForAccountsSelection = () => {
                const availableAccounts = buildAccountCandidateList();
                return props?.user?.userProperties?.filters[ currentFilter ]?.selection?.some( item => availableAccounts.includes( item ) );
            };
            const buildAccountCandidateList = () => {
                const reseller = extractReseller( payStation.box );
                const customer = extractCustomer( payStation.box );
                const resellerAccount = accounts.filter( item => item.code === reseller.code && item.type === AccountType.RESELLER );
                const customerAccount = accounts.filter( item => item.code === customer.code && item.type === AccountType.CUSTOMER );

                const availableAccounts = [];
                try {
                    if( resellerAccount[0] ) availableAccounts.push( `account-${resellerAccount[0].id}` );
                    if( customerAccount[0] ) availableAccounts.push( `account-${customerAccount[0].id}` );
                    let currentCandidate = ( resellerAccount[0] ) ? resellerAccount[0] : customerAccount[0];
                    while( currentCandidate?.createdby && currentCandidate.createdby > 0 ) {
                        const parentId = currentCandidate.createdby;
                        if( ! availableAccounts.includes( `account-${parentId}` ) ) {
                            availableAccounts.push( `account-${parentId}` );
                        }
                        currentCandidate = accounts.find( item => item.id === parentId );
                    }
                } catch ( error ) {
                    console.warn( error );
                }
                return availableAccounts;
            };

            if( props?.user?.userProperties?.filters[ currentFilter ]?.selection?.some( item => item === `box-${payStation.box?.id}` ) &&
                ! filtered.some( item => item === payStation ) ) {
                filtered.push( payStation );
            }

            if( shouldDisplayForAccountsSelection() &&
                ! filtered.some( item => item === payStation ) ) {
                filtered.push( payStation );
            }
        } );
        console.log( 'filtered' , filtered );
        return filtered;
    }

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
        if( props.onSearchMenuOpenedStateChanged ) {
            props.onSearchMenuOpenedStateChanged( event.currentTarget );
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCurrentSearch(null);
        if( props.onSearchMenuOpenedStateChanged ) {
            props.onSearchMenuOpenedStateChanged( null );
        }
    };

    const handleNewFilter = () => {
        isNewFilter = true;
        loadAccounts();
        setIsEditing( true );
    }

    const handleEditFilter = () => {
        if( currentFilter !== '-1' ) {
            loadAccounts();
            const filterIndex = parseInt( props?.user?.userProperties?.properties?.currentFilter , 10 );
            editingFilterName = props?.user?.userProperties?.filters[ filterIndex ].name;
            setIsEditing( true );
        }
    }

    const handleRemoveFilter = () => {
        const filterIndex = parseInt( props?.user?.userProperties?.properties?.currentFilter , 10 );
        setPendingDelete(props?.user?.userProperties?.filters[ filterIndex ].name );
    }

    const handleConfirm = ( mode , target ) => {
        if( mode === "delete" ) {
            props?.user?.userProperties?.updateProperty( 'currentFilter' , '-1' );
            setCurrentFilter( "-1" );
            removeFilter( target );
            setPendingDelete(null);
        }
    }

    const handleEditApply = () => {
        const name = document.querySelector('#name-field');
        if( name && name.value && name.value.trim() !== '' ) {
            const filterName = name.value.trim();
            const filterIndex = saveFilter( filterName );
            if( filterIndex >= 0 ) {
                selectFilter( `${filterIndex}` );
            }
            setIsEditing( false );
            setSelectedItems( [] );
            setExpandedItems( [] );
            editingFilterName = null;
            isNewFilter = false;
        }
    }

    const handleEditCancel = () => {
        setIsEditing( false );
        setSelectedItems( [] );
        setExpandedItems( [] );
    }

    const saveFilter = filterName => {
        shouldNotify = true;
        const hasFilter = props?.user?.userProperties?.filters?.some( item => item.name === filterName );
        if( isNewFilter ) {
            if( ! hasFilter ) {
                const newFilters = JSON.parse( JSON.stringify( props?.user?.userProperties?.filters ) );
                const filter = {
                    name: filterName,
                    selection: selectedItems,
                    expanded: expandedItems
                };
                newFilters.push( filter );
                props?.user?.userProperties?.updateFilters( newFilters );
                props?.user?.observer?.handleSnackbar( "success" , I18n.get("Filter added") );
                return newFilters.indexOf( filter );
            } else {
                props?.user?.observer?.handleSnackbar( "error" , I18n.get("Filter already exists") );
                return -1;
            }
        } else {
            let index = -1;
            props?.user?.userProperties?.filters?.forEach( candidate => {
                if( candidate.name === filterName ) {
                    candidate.selection = selectedItems;
                    index = props?.user?.userProperties?.filters?.indexOf( candidate );
                }
            } );
            return index;
        }
    }

    const selectFilter = indexAsString => {
        setCurrentFilter( indexAsString );
        const filterIndex = parseInt( indexAsString , 10 );
        if( filterIndex >= 0 ) {
            setSelectedItems( props?.user?.userProperties?.filters[ filterIndex ].selection );
            setExpandedItems( props?.user?.userProperties?.filters[ filterIndex ].expanded );
        }
        props?.user?.userProperties?.updateProperty( 'currentFilter' , filterIndex );
        shouldNotify = true;
    }

    const selectReseller = indexAsString => {
        setReseller( indexAsString );
        setCustomer( '-1' );
    }

    const selectCustomer = indexAsString => {
        setCustomer( indexAsString );
    }

    const removeFilter = filterName => {
        props?.user?.userProperties?.updateFilters( props?.user?.userProperties?.filters?.filter( item => item.name !== filterName ) );
    }

    const handleSelectedItemsChange = ( _event, ids ) => {
        setSelectedItems( ids );
    };

    const handleExpandedItemsChange = ( _event, ids ) => {
        setExpandedItems( ids );
    };

    const handleSelectPayStation = ( id , name , boxExtraData ) => {
        const copy = JSON.parse( JSON.stringify( selectedDynamicItems ) );
        const index = copy.findIndex( candidate => candidate.id === id );
        if (index !== -1) {
            // Item exists in the list, remove it
            copy.splice(index, 1);
        } else {
            // Item doesn't exist in the list, add it
            copy.push( { id: id , name: name , boxExtraData: boxExtraData } );
        }
        setSelectedDynamicItems( copy );
        shouldNotify = true;
    }

    const clearPayStationSelection = React.useCallback(() => {
        setSelectedDynamicItems( [] );
        shouldNotify = true;
        isClearRequested = true;
    }, []);

    const selectAllPayStations = React.useCallback(() => {
        const defaultSelection = [];
        const resellers = {};
        const customers = {};

        if( ! accounts || ! props.payStations ) {
            loadAccounts();
            return null;
        }

        accounts.forEach( account => {
            if( account.type === AccountType.RESELLER ) {
                resellers[account.id] = account.code;
            }

            if( account.type === AccountType.CUSTOMER ) {
                if( reseller === `${account.createdby}` || reseller === '-1') {
                    customers[account.id] = account.code;
                }
            }
        } );

        const finder = new PayStationFinder( props.payStations );
        props.payStations?.forEach( payStation => {
            const terminal = ( payStation.terminals && payStation.terminals.length > 0 ) ? payStation.terminals[0] : null;
            const boxExtraData = extractCompanyShopLabel( finder.buildTerminalExtraData( terminal ) );
            if( reseller && parseInt( reseller , 10 ) > -1 ) {
                if( customer && parseInt( customer , 10 ) > -1 ) {
                    if( payStation?.box?.informations?.sender?.reseller_id === resellers[reseller] &&
                        payStation?.box?.informations?.sender?.customer_code === customers[customer] &&
                        payStationMatchesFilter( payStation, currentSearch, accounts ) ) {
                        defaultSelection.push( { id: payStation.box?.id , name: payStation?.box?.informations?.attributes?.name , boxExtraData: boxExtraData } );
                    }
                } else {
                    if( payStation?.box?.informations?.sender?.reseller_id === resellers[reseller] &&
                        payStationMatchesFilter( payStation, currentSearch, accounts ) ) {
                        defaultSelection.push( { id: payStation.box?.id , name: payStation?.box?.informations?.attributes?.name , boxExtraData: boxExtraData } );
                    }
                }
            } else {
                if( customer && parseInt( customer , 10 ) > -1 ) {
                    if( payStation?.box?.informations?.sender?.customer_code === customers[customer] &&
                        payStationMatchesFilter( payStation, currentSearch, accounts ) ) {
                        defaultSelection.push( { id: payStation.box?.id , name: payStation?.box?.informations?.attributes?.name , boxExtraData: boxExtraData } );
                    }
                } else {
                    if( payStationMatchesFilter( payStation, currentSearch, accounts ) ) {
                        defaultSelection.push( { id: payStation.box?.id , name: payStation?.box?.informations?.attributes?.name , boxExtraData: boxExtraData } );
                    }
                }
            }
        } )
        setSelectedDynamicItems( defaultSelection );
        shouldNotify = true;
    }, [accounts, props.payStations, reseller, customer, currentSearch, loadAccounts]);

    const isSubmitEnabled = () => {
        const name = document.querySelector('#name-field');
        if( name && name.value && name.value.trim() !== '' ) {
            return selectedItems.length > 0;
        }
        return false;
    }

    const findAvailableFilters = () => {
        const availableFilters = [];
        props?.user?.userProperties?.filters?.forEach( (filter, index) => {
            const value = `${index}`;
            availableFilters.push( <MenuItem key={`filter-${value}`} value={value}>{filter.name}</MenuItem> );
        } );

        return availableFilters;
    };

    const buildTree = () => {
        const tree = [];
        const rootLevel = {
            id: `account-${props.user?.observer?.currentUser?.accountId}`,
            label: `${props.user?.observer?.currentUser?.accountLabel}`,
            children: []
        };
        const quickPerfAccess = {};
        quickPerfAccess[`account-${props.user?.observer?.currentUser?.accountId}`] = rootLevel;

        const addIfNotExists = account => {
            if( account && ! quickPerfAccess.hasOwnProperty( `account-${account.id}` ) ) {
                const treeItems = {
                    id: `account-${account.id}`,
                    label: `${account.name}`,
                    children: []
                }
                if( account.createdby ) {
                    quickPerfAccess[`account-${account.createdby}`]?.children.push( treeItems );
                    quickPerfAccess[`account-${account.id}`] = treeItems;
                }
            }
        }

        if( props.payStations && accounts ) {
            const activePayStations = configAnalyzer.getDistinctActiveBoxCBMSList();
            props.payStations.forEach( payStation => {
                if( activePayStations.includes( `4_${payStation.box.informations?.bms}` ) ) {
                    const reseller = extractReseller( payStation.box );
                    const customer = extractCustomer( payStation.box );
                    const resellerAccount = accounts.filter( item => item.code === reseller.code && item.type === AccountType.RESELLER );
                    const customerAccount = accounts.filter( item => item.code === customer.code && item.type === AccountType.CUSTOMER );

                    try {
                        addIfNotExists( resellerAccount[0] );
                        addIfNotExists( customerAccount[0] );
                        if( ! quickPerfAccess.hasOwnProperty( `box-${payStation?.box?.id}` ) ) {
                            const treeItems = {
                                id: `box-${payStation?.box?.id}`,
                                label: `${ ( payStation?.box?.informations?.attributes?.name ) ? payStation?.box?.informations?.attributes?.name : payStation?.box?.name }`,
                                children: []
                            }
                            if( customerAccount && customerAccount.length > 0 ) {
                                quickPerfAccess[`account-${customerAccount[0].id}`]?.children.push( treeItems );
                            } else if ( resellerAccount && resellerAccount.length > 0 ) {
                                quickPerfAccess[`account-${resellerAccount[0].id}`]?.children.push( treeItems );
                            } else {
                                console.log( 'CAN NOT HAPPEN IF SO YOU SUCKS DEV !!!!' );
                            }
                            quickPerfAccess[`box-${payStation?.box?.id}`] = treeItems;

                            payStation.devices.forEach( device => {
                                const type = device.informations?.identity?.model;
                                if( ! quickPerfAccess.hasOwnProperty( `device-${type}-${device?.id}` ) ) {
                                    treeItems.children.push( {
                                        id: `device-${type}-${device?.id}`,
                                        label: `${device?.name}`,
                                        children: []
                                    } );
                                    quickPerfAccess[`device-${type}-${device?.id}`] = `${device?.name}`;
                                }
                            } );
                        }
                    } catch ( error ) {

                    }
                }
            } );
        }
        tree.push( rootLevel );
        return tree;
    }

    const renderTriggerElement = () => {
        if( props.variant ) {
            //manage variant display
            if( props.variant === "select" ) {
                let properties = {
                    id:"node",
                    allowEmpty:false,
                    label:I18n.get("Select node"),
                    defaultValue:"",
                    onValueChanged:null,
                    isHashMapOption:false,
                    options:[""]
                }

                if( props.SelectProps ) {
                    properties = props.SelectProps;
                }

                return (
                    <SelectHidingMenu 	  id={properties.id}
                                          label={properties.label}
                                          allowEmpty={properties.allowEmpty}
                                          defaultValue={properties.defaultValue}
                                          onValueChanged={properties.onValueChanged}
                                          isDarkStyle={props.isDarkStyle}
                                          isHashMapOption={properties.isHashMapOption}
                                          handleClick={handleClick}
                                          disabled={properties.disabled}
                                          options={properties.options}  />
                );
            }

        }
        //default display return a button with a search icon !!!
        const filterIndex = parseInt( props?.user?.userProperties?.properties?.currentFilter , 10 );
        const reminder = ( currentFilter !== '-1' ) ?  props?.user?.userProperties?.filters[ filterIndex ].name  : I18n.get('No filter selected');
        return (
            <div className={`reminder-filter`}>
                <div className={`filter-name`}>{(isSimpleMode) ? '' : reminder}</div>
                <IconButton key="search-icon Icon"
                            className="search-icon Icon"
                            disableRipple
                            onClick={handleClick} >
                    {/*<FontAwesomeIcon icon="fa-brands fa-searchengin" />*/}
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                </IconButton>
            </div>
        );
    }

    const renderSearchBar = () => {
        if( isSimpleMode ) {
            return (
                <div className={`filter-bar combo-block`}>
                    <SearchBar
                        id="search-user"
                        onChange={ evt => setCurrentSearch( evt.target.value ) }
                        onClear={ () => setCurrentSearch( '' ) }
                    />
                </div>
            );
        }
        return null;
    }

    const renderSearchContent = () => {
        const key = ( isSimpleMode ) ? 'Simple filter management' : 'Advanced filter management';
        return (
            <div className="dropdown-menu-md overflow-hidden p-3 cm-search-menu">
                <div className="grid-menu grid-menu-1col">
                    <div className="title">
                        <span>{I18n.get( key )}</span>
                        {renderModeSelector()}
                    </div>
                    {renderSearchBar()}
                    {renderSelectedFilter()}
                    {renderFilterEditingMode()}
                </div>
            </div>
        );
    }

    const renderModeSelector = () => {
        // Advanced mode is not available until its selection flow is reviewed.
        return null;
    }

    const renderPopover = () => {
        return (
            <Popover anchorEl={anchorEl}
                     key={`search-engine`}
                     keepMounted
                     anchorOrigin={{
                         vertical: 'bottom',
                         horizontal: 'left',
                     }}
                     transformOrigin={{
                         vertical: 'top',
                         horizontal: 'right',
                     }}
                     open={Boolean(anchorEl)}
                     classes={{ paper: cssMenu }}
                     onClose={handleClose} >
                {renderSearchContent ()}
            </Popover>
        );
    }

    const renderSelectedFilter = () => {
        if( isSimpleMode ) {
            return renderSimpleMode();
        } else {
            const availableFilters = findAvailableFilters();
            return (
                <div className={`filter-row`}>
                    <Select
                        variant="outlined"
                        className={`filter-selector`}
                        value={currentFilter}
                        onChange={ event => selectFilter( event.target.value )}
                    >
                        <MenuItem key="no-filter" value="-1">{I18n.get('No filter selected')}</MenuItem>
                        {availableFilters.map( filter  => filter )}
                    </Select>
                    <div className={`button`}  onClick={handleNewFilter}>
                        { I18n.get("Create filter") }
                    </div>
                    <div className={`button ${( currentFilter === '-1' ) ? 'disabled' : ''}`}  onClick={handleEditFilter}>
                        { I18n.get("Edit filter") }
                    </div>
                    <div className={`button ${( currentFilter === '-1' ) ? 'disabled' : ''}`}  onClick={handleRemoveFilter}>
                        { I18n.get("Remove filter") }
                    </div>
                </div>
            );
        }
    };

    const renderFilterEditingMode = () => {
        if( ! isEditing ) {
            return null;
        }
        const tree = buildTree();
        const treeItems = [];
        tree.forEach( item => {
            treeItems.push( renderTreeItem( item ) );
        } );
        return (
            <div className={`filter-editing-mode`}>
                <Box sx={{ minHeight: 200, flexGrow: 1, maxHeight: 500, overflowY: 'auto' }}>
                    <div className={`filter-header`}>
                        <div className="name">
                            <TextField
                                id="name-field"
                                label={I18n.get("Filter name")}
                                defaultValue={(currentFilter !== '-1' && !isNewFilter ) ? editingFilterName : ''}
                                value={filterName}
                                variant="outlined"
                                /*autoComplete="off"*/
                                disabled={(currentFilter !== '-1' && !isNewFilter)}
                                onBlur={(evt) => setFilterName( evt.target.value ) }
                            />
                        </div>
                        <div className={`filter-selected-count`}>
                            {selectedItems.length}
                            <FontAwesomeIcon icon={faClipboard} />
                        </div>
                    </div>
                    <SimpleTreeView multiSelect
                                    selectedItems={selectedItems}
                                    expandedItems={expandedItems}
                                    onExpandedItemsChange={handleExpandedItemsChange}
                                    onSelectedItemsChange={handleSelectedItemsChange}>
                        {treeItems.map( item => item )}
                    </SimpleTreeView>
                </Box>
                <div className={`user-actions`}>
                    <div className={`button ${( isSubmitEnabled() ) ? '' : 'disabled'}`}  onClick={handleEditApply}>
                        { I18n.get("Apply") }
                    </div>
                    <div className={`button`}  onClick={handleEditCancel}>
                        { I18n.get("Cancel") }
                    </div>
                </div>
            </div>

        );
    }

    /**
     * @param {import('../../../Models/DomainTypes').PayStation} payStation
     * @param {string} boxExtraData
     */
    const renderPayStationItem = ( payStation , boxExtraData ) => {
        const extraCss = ( selectedDynamicItems.findIndex( candidate => candidate.id === payStation?.box?.id ) !== -1 ) ? 'selected' : '';
        return (
            <div key={`pay-station-${payStation?.box?.id}`}
                 className={`clickable line-payStation ${extraCss}`}
                 onClick={() => {
                     handleSelectPayStation( payStation?.box?.id , payStation?.box?.informations?.attributes?.name , boxExtraData );
                 }}>
                <div className={`name`}>{payStation?.box?.informations?.attributes?.name}</div>
                <div className={`extra-info`}>{boxExtraData}</div>
            </div>
        );
    }

    const renderSimpleMode = () => {
        const resellers = [];
        const customers = [];
        const identification = {
            resellerId: null,
            customerCode: null
        };

        if( ! accounts ) {
            return null;
        }

        accounts.forEach( account => {
            if( account.type === AccountType.RESELLER ) {
                resellers.push( <MenuItem key={`reseller-${account.id}`} value={`${account.id}`}>{account.name}</MenuItem> );
                if( reseller === `${account.id}`) {
                    identification.resellerId = account.code;
                }
            }

            if( account.type === AccountType.CUSTOMER ) {
                if( reseller === `${account.createdby}` || reseller === '-1') {
                    customers.push( <MenuItem key={`customer-${account.id}`} value={`${account.id}`}>{account.name}</MenuItem> );
                    if( customer === `${account.id}` ) {
                        identification.customerCode = account.code;
                    }
                }
            }
        } );

        const finder = new PayStationFinder( props.payStations );
        const payStationsForSelection = [];
        props.payStations?.forEach( payStation => {
            if( ( payStation?.box?.informations?.sender?.reseller_id === identification.resellerId || reseller === '-1' ) &&
                ( payStation?.box?.informations?.sender?.customer_code === identification.customerCode || customer === '-1' ) ) {
                const terminal = ( payStation.terminals && payStation.terminals.length > 0 ) ? payStation.terminals[0] : null;
                const boxExtraData = extractCompanyShopLabel( finder.buildTerminalExtraData( terminal ) );
                if( payStationMatchesFilter( payStation, currentSearch, accounts ) ) {
                    payStationsForSelection.push( renderPayStationItem( payStation , boxExtraData ) );
                }
            }
        } );

        const selectedPayStations = [];
        selectedDynamicItems.forEach( item => {
            selectedPayStations.push( <div key={`selected-pay-station-${item.id}`} className={`box-display`}> <div>{item.name}</div> <div className={`displayed extra-info`}> ( {(item.boxExtraData)?item.boxExtraData:''} ) </div></div> );
        } );

        return (
            <div className={`simple-mode-filters`}>
                <div className={`selectors`}>
                    <div className={`combo-container`}>
                        <div className={`combo-block`}>
                            <Select
                                variant="outlined"
                                className={`filter-selector`}
                                value={reseller}
                                disabled={props.user.observer.currentUser.role === UserRoles.SALE_ROLE}
                                onChange={ event => selectReseller( event.target.value )}
                            >
                                <MenuItem key="all-resellers" value="-1">{I18n.get('All')}</MenuItem>
                                {resellers.map( reseller  => reseller )}
                            </Select>
                            <div className={`combo-label`}> {I18n.get('Reseller')} </div>
                        </div>
                        <div className={`combo-block`}>
                            <Select
                                variant="outlined"
                                className={`filter-selector`}
                                value={customer}
                                disabled={props.user.observer.currentUser.role === UserRoles.SALE_ROLE}
                                onChange={ event => selectCustomer( event.target.value )}
                            >
                                <MenuItem key="all-customers" value="-1">{I18n.get('All')}</MenuItem>
                                {customers.map( customer  => customer )}
                            </Select>
                            <div className={`combo-label`}> {I18n.get('Customer')} </div>
                        </div>
                    </div>
                    <div className={`box-container`}>
                        <div className={`section-title`}>{I18n.get('Available')}</div>
                        <div className={`separator`} ></div>
                        <div className={`content`} >
                            {payStationsForSelection.map( payStation => payStation )}
                        </div>
                    </div>
                </div>
                <div className={`selection`}>
                    <div className={`section-title`}>
                        <span>{I18n.get('Selected')}</span>
                        <FontAwesomeIcon icon={faListCheck}
                                         className={`quick-trash`}
                                         onClick={selectAllPayStations}/>
                        {/*<FontAwesomeIcon icon="fa-solid fa-check-double"
                                         className={`quick-trash`}
                                         onClick={selectAllPayStations}/>*/}
                        <FontAwesomeIcon icon={faTrash}
                                         className={`quick-trash`}
                                         onClick={clearPayStationSelection}/>
                    </div>
                    <div className={`separator`} ></div>
                    <div className={`content`} >
                        {selectedPayStations.map( payStation => payStation )}
                    </div>
                </div>
            </div>
        );
    }

    const StyledTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
        padding: theme.spacing(0.5, 1),
    }));

    const StyledTreeItem = React.forwardRef(/** @param {FilterTreeItemProps} itemProps */ function CustomTreeItem(itemProps, ref) {
        const { id, itemId, label, disabled, children, ...other } = itemProps;

        const {
            getRootProps,
            getContentProps,
            getIconContainerProps,
            getLabelProps,
            getGroupTransitionProps,
            status,
        } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

        const resolveIcon = idItem => {
            if( idItem.startsWith( 'account-' ) ) {
                const idAccount = parseInt( idItem.replace( 'account-' , '' ) , 10 );
                if( idAccount !== props.user?.observer?.currentUser?.accountId ) {
                    let type = null;
                    if( accounts ) {
                        accounts.forEach( candidate => {
                            if( candidate.id === idAccount ) {
                                type = candidate.type;
                            }
                        } );
                        if( type === AccountType.RESELLER ) {
                            return getIconForNode( {role: Roles.RESELLER} );
                        } else if ( type === AccountType.CUSTOMER ) {
                            return getIconForNode( {role: Roles.CUSTOMER} );
                        }
                    }
                }
            } else if ( idItem.startsWith( 'box-' ) ) {
                return getIconForNode( {role: Roles.BOX} );
            } else if ( idItem.startsWith( 'device-' ) ) {
                if( idItem.includes( 'BNR' ) || idItem.includes( 'SCR' ) ) {
                    return fundType.getIcon( fundType.CASH_NOTE );
                } else {
                    return fundType.getIcon( fundType.CASH_COIN );
                }
            }
            return faHouse;
        };
        const icon = resolveIcon( itemId );

        return (
            <TreeItem2Provider itemId={itemId}>
                <TreeItem2Root {...getRootProps(other)}>
                    <StyledTreeItemContent {...getContentProps()} className={`${(selectedItems.some( item => item === itemId )) ? 'selected item-content' : 'item-content'}`}>
                        <TreeItem2IconContainer {...getIconContainerProps()}>
                            <TreeItem2Icon status={status} />
                        </TreeItem2IconContainer>
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                            <FontAwesomeIcon icon={icon}/>
                            <TreeItem2Label {...getLabelProps()} />
                        </Box>
                    </StyledTreeItemContent>
                    {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
                </TreeItem2Root>
            </TreeItem2Provider>
        );
    });

    const renderTreeItem = element => {
        const children = [];
        element.children.forEach( child => {
            children.push( renderTreeItem( child ) );
        } );
        return (
            <StyledTreeItem key={element.id}
                            itemId={element.id}
                            label={element.label}
                            disabled={(element.id.startsWith('device-')) }>
                {children.map( item => item )}
            </StyledTreeItem>
        );
        //using treeItem1 not icon available
        /*return (
            <TreeItem key={element.id} itemId={element.id} label={element.label} className={`Mui-expanded`}>
                {children.map( item => item )}
            </TreeItem>
        );*/
    }

    const renderConfirmDialog = () => {
        return (
            <DialogConfirmRemove handleClose={handleConfirm}
                                 pendingDelete={pendingDelete}
                                 isDarkStyle={props.isDarkStyle}/>
        );
    }

    React.useEffect( () => {
        if( ! accounts ) {
            loadAccounts();
        }
    }, [accounts, loadAccounts]);

    React.useEffect( () => {
        if( props?.user?.userProperties?.properties?.currentFilter &&
            props?.user?.userProperties?.properties?.currentFilter !== currentFilter ) {
            setCurrentFilter( props?.user?.userProperties?.properties?.currentFilter );
            if( props?.user?.userProperties?.filters[ filterIndex ] ) {
                setSelectedItems( props?.user?.userProperties?.filters[ filterIndex ].selection );
                setExpandedItems( props?.user?.userProperties?.filters[ filterIndex ].expanded );
            }
        }

        if( isSimpleMode && props.payStations && props.payStations.length > 0 && selectedDynamicItems.length === 0 && ! isClearRequested ) {
            selectAllPayStations();
        }

        if( ( parseInt( reseller , 10 ) > -1 && reseller !== previousReseller ) ||
            ( parseInt( customer , 10 ) > -1 && customer !== previousCustomer ) ) {
            previousReseller = reseller;
            previousCustomer = customer;
            clearPayStationSelection();
            selectAllPayStations();
        }
    } , [
        props.user?.userProperties?.properties?.currentFilter,
        props.user?.userProperties?.filters,
        props.payStations,
        currentFilter,
        isSimpleMode,
        selectedDynamicItems.length,
        filterIndex,
        selectAllPayStations,
        clearPayStationSelection,
        reseller,
        customer
    ] );

    let css = "search";
    let cssMenu = "p-0"
    if( props.isDarkStyle ) {
        css += " dark";
        cssMenu += " dark";
    }

    props.registerFilterDelegate( filter );

    if( shouldNotify && props.onFilterUpdated ) {
        shouldNotify = false;
        props.onFilterUpdated( filter() );
    }

    return (
        <React.Fragment>
            <div className={css}>
                {renderTriggerElement()}
                {renderPopover()}
                {renderConfirmDialog()}
            </div>
        </React.Fragment>
    );
};

export default UserFilteringEngine;
